import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { CAlert } from '@coreui/react';
import { DataTable, useTableState } from '@trading/ui-table';
import type { SortState } from '@trading/ui-table';
import { POSITIONS_FILTERS, buildPositionsColumns, decisionRowStyle } from './columns.js';
import type { PositionRow } from './types.js';

export interface PositionsTableProps {
    rows: PositionRow[];
    /** Omit to hide the Close column. */
    onClose?: (row: PositionRow) => void;
    /** Omit to hide the Set Target column. */
    onSetTarget?: (symbol: string, target: number | null) => void;
    /** Omit to render Entry Date as read-only text instead of an editor. */
    onSetEntryDate?: (symbol: string, entryDate: string | null) => void;
    closingSymbol?: string | null;
    pendingCloseSymbols?: ReadonlySet<string>;
    /** Renders `…` instead of `—` in the ATR cells while the ATR cache is still loading. */
    atrLoading?: boolean;
    /** Row selection. When omitted the table keeps its own local selection. */
    selectedSymbols?: Set<string>;
    onToggleSelect?: (symbol: string, checked: boolean) => void;
    onToggleSelectAll?: (checked: boolean) => void;
    /** Message shown in place of the table when `rows` is empty. */
    emptyMessage?: string;
}

const DEFAULT_SORT: SortState = { key: 'symbol', direction: 'asc' };

/**
 * The shared positions table: one set of columns, sorting, filtering and row selection for
 * every consumer. Purely presentational — it fetches nothing and owns no timers. Which
 * interactive columns appear is decided by which callbacks you pass (see
 * `buildPositionsColumns`), so a consumer without override endpoints gets the same table
 * minus those editors.
 *
 * Requires a react-router context, since sort/filter state persists to URL params.
 */
export const PositionsTable = ({
    rows,
    onClose,
    onSetTarget,
    onSetEntryDate,
    closingSymbol = null,
    pendingCloseSymbols,
    atrLoading = false,
    selectedSymbols,
    onToggleSelect,
    onToggleSelectAll,
    emptyMessage = 'No open positions.',
}: PositionsTableProps): ReactElement => {
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set());

    const columns = useMemo(
        () =>
            buildPositionsColumns({
                onClose,
                onSetTarget,
                onSetEntryDate,
                closingSymbol,
                pendingCloseSymbols,
                atrLoading,
            }),
        [onClose, onSetTarget, onSetEntryDate, closingSymbol, pendingCloseSymbols, atrLoading],
    );

    const { sortState, filterState, filteredRows, toggleSort, setFilter, clearFilters } =
        useTableState<PositionRow>(rows, columns, POSITIONS_FILTERS, DEFAULT_SORT);

    const handleToggleSelect = useCallback(
        (symbol: string, checked: boolean) => {
            if (onToggleSelect) {
                onToggleSelect(symbol, checked);
                return;
            }
            setLocalSelected((prev) => {
                const next = new Set(prev);
                if (checked) next.add(symbol);
                else next.delete(symbol);
                return next;
            });
        },
        [onToggleSelect],
    );

    const handleToggleSelectAll = useCallback(
        (checked: boolean) => {
            if (onToggleSelectAll) {
                onToggleSelectAll(checked);
                return;
            }
            setLocalSelected(checked ? new Set(filteredRows.map((r) => r.symbol)) : new Set());
        },
        [onToggleSelectAll, filteredRows],
    );

    if (rows.length === 0) {
        return <CAlert color="info">{emptyMessage}</CAlert>;
    }

    return (
        <DataTable
            data={filteredRows}
            columns={columns}
            filters={POSITIONS_FILTERS}
            sortState={sortState}
            filterState={filterState}
            onSort={toggleSort}
            onFilter={setFilter}
            onClearFilters={clearFilters}
            selectedRows={selectedSymbols ?? localSelected}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            getRowKey={(row) => row.symbol}
            rowStyle={decisionRowStyle}
        />
    );
};
